<script setup lang="ts">
import { computed } from 'vue';
import { SCHEDULE_KIND_LABELS, scheduleDetailIcons, type ScheduleSummary } from '@/utils/customPlaylistSchedule';
import ScheduleQualifier from './ScheduleQualifier.vue';
import ScheduleTypeIcon from './ScheduleTypeIcon.vue';

/**
 * A schedule's detail line: the icons, then the type and any qualifier. An
 * insert, message, silence or station change leads with its type icon and then
 * the one-off or recurring icon. A playlist has only the one-off or recurring
 * icon, because that is its type. The dates sit on the line above.
 *
 * The icons keep to their own column, so a qualifier that wraps stays indented
 * under the text rather than under the icons.
 */
const props = defineProps<{ summary: ScheduleSummary }>();

const icons = computed(() => scheduleDetailIcons(props.summary));
const label = computed(() => SCHEDULE_KIND_LABELS[props.summary.kind]);
</script>

<template>
    <span class="text-muted-foreground flex min-w-0 items-start gap-1.5 text-xs" data-testid="schedule-detail-line">
        <span class="flex h-4 shrink-0 items-center gap-1" data-testid="schedule-detail-icons">
            <!-- The type is written out beside the icons, so only the cadence icon is read aloud. -->
            <ScheduleTypeIcon v-if="icons.type" :kind="icons.type" decorative />
            <ScheduleTypeIcon v-if="icons.cadence" :kind="icons.cadence" :decorative="icons.type === null" />
        </span>
        <span class="min-w-0 leading-4">{{ label }}<template v-if="summary.qualifierParts.length > 0">{{ ' · ' }}<ScheduleQualifier :parts="summary.qualifierParts" /></template></span>
        <slot />
    </span>
</template>
